import { Rows3, ScanLine } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';

import type { EnrichmentValidationFieldChange } from '../../types/enrichment-validation.api.types';
import {
  type DiffSegment,
  diffKeep,
  diffText,
  makeVisible,
  pairChanged,
  toLines,
} from '../../utils/value-diff';

/** Any stored value as one line of text: strings as they are, everything else as JSON. */
function asText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return '';
  return JSON.stringify(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Whether a value carries nothing (what an emptied field normalises to). */
function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (isRecord(value)) return Object.keys(value).length === 0;
  return false;
}

type Side = 'before' | 'after';

/** The highlight of a run only one side has: removed (before) or added (after). */
const MARK_CLASS: Record<Side, string> = {
  before: 'bg-error/15 text-error rounded-[2px] ring-1 ring-error/30',
  after: 'bg-success/25 text-foreground rounded-[2px] ring-1 ring-success/40',
};

/** The small "Line 4" / "Item 2" / "Row 3" label of the changes-only view. */
function Position({ children }: { children: string }) {
  return (
    <span className="text-muted-foreground mr-2 inline-block min-w-[3.25rem] font-mono text-[10px] tracking-wide uppercase select-none">
      {children}
    </span>
  );
}

/** Text with the runs only this side has highlighted, invisible characters made visible. */
function Marked({ segments, side }: { segments: DiffSegment[]; side: Side }) {
  return (
    <>
      {segments.map((segment, index) =>
        segment.changed ? (
          // Segments are a static split of one value; the index is their identity.
          <mark key={String(index)} className={MARK_CLASS[side]}>
            {segment.text === '' ? <EmptyMark /> : makeVisible(segment.text)}
          </mark>
        ) : (
          <span key={String(index)}>{segment.text}</span>
        ),
      )}
    </>
  );
}

/** What an emptied (or added-empty) entry shows inside its highlight, so it is never blank. */
function EmptyMark() {
  const { t } = useTranslation('catalog-operations');
  return <span className="italic">{t('enrichmentValidation.drawer.changes.emptyEntry')}</span>;
}

/** A changed entry (item, cell, key line): compared word by word with the entry it replaced
 *  when there is one — so a whitespace-only edit inside it shows — else highlighted whole. */
function ChangedText({
  text,
  counterpart,
  side,
}: {
  text: string;
  counterpart: string | undefined;
  side: Side;
}) {
  if (counterpart === undefined || counterpart === text) {
    return (
      <mark className={MARK_CLASS[side]}>{text === '' ? <EmptyMark /> : makeVisible(text)}</mark>
    );
  }
  const diff = side === 'before' ? diffText(text, counterpart) : diffText(counterpart, text);
  return <Marked segments={side === 'before' ? diff.before : diff.after} side={side} />;
}

/** "Show only changes" with nothing on this side: what that means for this side. */
function NothingHere({ side }: { side: Side }) {
  const { t } = useTranslation('catalog-operations');
  return (
    <span className="text-muted-foreground block text-xs italic">
      {side === 'before'
        ? t('enrichmentValidation.drawer.changes.nothingRemoved')
        : t('enrichmentValidation.drawer.changes.nothingAdded')}
    </span>
  );
}

/** One text compared with the other side's: the differing words / spaces highlighted. With
 *  `onlyChanges`, only the lines that changed, each with its line number. */
function TextDiff({
  text,
  other,
  side,
  onlyChanges,
}: {
  text: string;
  other: string;
  side: Side;
  onlyChanges: boolean;
}) {
  const { t } = useTranslation('catalog-operations');
  const diff = side === 'before' ? diffText(text, other) : diffText(other, text);
  const segments = side === 'before' ? diff.before : diff.after;
  if (!onlyChanges) return <Marked segments={segments} side={side} />;
  const lines = toLines(segments).filter((line) => line.changed);
  if (lines.length === 0) return <NothingHere side={side} />;
  return (
    <span className="block space-y-1">
      {lines.map((line) => (
        <span key={line.number} className="block">
          <Position>{t('enrichmentValidation.drawer.changes.line', { n: line.number })}</Position>
          {line.segments.length > 0 ? <Marked segments={line.segments} side={side} /> : null}
        </span>
      ))}
    </span>
  );
}

/** How an entry is compared: a row by its JSON (key and type included), anything else by its
 *  type and text — so `5` and `"5"` differ, as they do to the normaliser. */
function entryKey(entry: unknown): string {
  return typeof entry === 'string' ? `s:${entry}` : `j:${JSON.stringify(entry)}`;
}

/** This side's entries against the other side's: which changed, and the other side's entry each
 *  changed one replaced (compared before → after either way, so both sides agree). Every entry
 *  is changed, unpaired, when the two are too far apart to compare. */
function compareEntries(
  values: readonly unknown[],
  others: readonly unknown[],
  side: Side,
): { changed: boolean[]; counterpart: (index: number) => unknown } {
  const mine = values.map(entryKey);
  const theirs = others.map(entryKey);
  const keep = side === 'before' ? diffKeep(mine, theirs) : diffKeep(theirs, mine);
  if (!keep) return { changed: values.map(() => true), counterpart: () => undefined };
  const changed = side === 'before' ? keep.a : keep.b;
  const { aToB, bToA } = pairChanged(keep.a, keep.b);
  const pairs = side === 'before' ? aToB : bToA;
  return {
    changed,
    counterpart: (index) => {
      const other = pairs.get(index);
      return other === undefined ? undefined : others[other];
    },
  };
}

interface ViewProps {
  value: unknown;
  other: unknown;
  side: Side;
  onlyChanges: boolean;
}

const DETAIL_SLOTS = ['section_name', 'attribute_name', 'attribute_value'] as const;

/** A row's other keys (aliases, unknown ones) as one line, shown after its three slots. */
function extraLine(row: Record<string, unknown>): string {
  return Object.entries(row)
    .filter(([key]) => !(DETAIL_SLOTS as readonly string[]).includes(key))
    .map(([key, item]) => `${key}: ${asText(item)}`)
    .join(' · ');
}

/** Detail rows as a table. A changed row is compared cell by cell with the row it replaced
 *  (only the cells that differ highlighted); a row only this side has is highlighted whole. A
 *  stored entry that is not a row (a JSON string, junk) gets a full-width row of its own text.
 *  With `onlyChanges`, only changed rows, numbered. */
function DetailTable({ value, other, side, onlyChanges }: ViewProps & { value: unknown[] }) {
  const { t } = useTranslation('catalog-operations');
  const { changed, counterpart } = compareEntries(value, Array.isArray(other) ? other : [], side);
  const shown = value
    .map((entry, index) => ({ entry, index, changed: changed[index] ?? false }))
    .filter((item) => !onlyChanges || item.changed);
  if (shown.length === 0) return <NothingHere side={side} />;
  const cellClass = 'py-0.5 pr-2 wrap-anywhere whitespace-pre-wrap';
  return (
    <div className="overflow-x-auto">
      <table
        data-testid={`batch-detail-validation-drawer-changes-detail-table-${side}`}
        className="w-full table-fixed text-left text-xs"
      >
        <thead className="text-muted-foreground">
          <tr>
            {onlyChanges && <th className="w-14 py-0.5 pr-2 font-medium" />}
            <th className="py-0.5 pr-2 font-medium">
              {t('enrichmentValidation.drawer.changes.section')}
            </th>
            <th className="py-0.5 pr-2 font-medium">
              {t('enrichmentValidation.drawer.changes.attribute')}
            </th>
            <th className="py-0.5 font-medium">{t('enrichmentValidation.drawer.changes.value')}</th>
          </tr>
        </thead>
        <tbody className="align-top">
          {shown.map(({ entry, index, changed: isChanged }) => {
            const pair = isChanged ? counterpart(index) : undefined;
            // One cell's text, highlighted against the same cell of the row it replaced.
            const show = (text: string, pairText: string | undefined) =>
              !isChanged || (pair !== undefined && pairText === text) ? (
                text
              ) : text === '' && pair !== undefined ? null : (
                <ChangedText text={text} counterpart={pairText} side={side} />
              );
            return (
              // Rows are a static snapshot (never reordered); the index is their identity.
              <tr key={String(index)} className="border-border/60 border-t">
                {onlyChanges && (
                  <td className="py-0.5 pr-2">
                    <Position>
                      {t('enrichmentValidation.drawer.changes.row', { n: index + 1 })}
                    </Position>
                  </td>
                )}
                {isRecord(entry) ? (
                  <>
                    {DETAIL_SLOTS.map((slot) => (
                      <td key={slot} className={cellClass}>
                        {slot in entry || (isRecord(pair) && slot in pair)
                          ? show(
                              asText(entry[slot]),
                              isRecord(pair) ? asText(pair[slot]) : undefined,
                            )
                          : null}
                        {slot === 'attribute_value' && extraLine(entry) !== '' ? (
                          <span className="text-muted-foreground block">
                            {show(extraLine(entry), isRecord(pair) ? extraLine(pair) : undefined)}
                          </span>
                        ) : null}
                      </td>
                    ))}
                  </>
                ) : (
                  <td colSpan={3} className={cn(cellClass, 'font-mono')}>
                    {show(asText(entry), pair === undefined ? undefined : asText(pair))}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** A list as items. A changed item is compared word by word with the item it replaced; an item
 *  only this side has is highlighted whole. With `onlyChanges`, only changed items, numbered. */
function ItemList({ value, other, side, onlyChanges }: ViewProps & { value: unknown[] }) {
  const { t } = useTranslation('catalog-operations');
  const { changed, counterpart } = compareEntries(value, Array.isArray(other) ? other : [], side);
  const shown = value
    .map((item, index) => ({ item: asText(item), index, changed: changed[index] ?? false }))
    .filter((entry) => !onlyChanges || entry.changed);
  if (shown.length === 0) return <NothingHere side={side} />;
  return (
    <ol className={cn('space-y-0.5 text-xs', !onlyChanges && 'list-decimal pl-4')}>
      {shown.map(({ item, index, changed: isChanged }) => {
        const pair = counterpart(index);
        return (
          // A static snapshot of the stored list; the index is its identity.
          <li key={String(index)} className="wrap-anywhere whitespace-pre-wrap">
            {onlyChanges && (
              <Position>{t('enrichmentValidation.drawer.changes.item', { n: index + 1 })}</Position>
            )}
            {isChanged ? (
              <ChangedText
                text={item}
                counterpart={pair === undefined ? undefined : asText(pair)}
                side={side}
              />
            ) : (
              item
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** One stored value, read-only, as text nodes, compared with the other side: detail rows (or a
 *  list either side of which holds rows) as a table, a list as items, a structured object as its
 *  content plus its other keys, anything else as text. */
function ValueView({ value, other, side, onlyChanges }: ViewProps) {
  const { t } = useTranslation('catalog-operations');
  if (isEmptyValue(value)) {
    return (
      <p className="text-muted-foreground text-xs italic">
        {t('enrichmentValidation.drawer.changes.empty')}
      </p>
    );
  }
  if (
    Array.isArray(value) &&
    (value.some(isRecord) || (Array.isArray(other) && other.some(isRecord)))
  ) {
    return <DetailTable value={value} other={other} side={side} onlyChanges={onlyChanges} />;
  }
  if (Array.isArray(value)) {
    return <ItemList value={value} other={other} side={side} onlyChanges={onlyChanges} />;
  }
  if (isRecord(value)) {
    const { content, ...rest } = value;
    const otherRecord = isRecord(other) ? other : {};
    const keys = Object.entries(rest)
      .map(([key, item]) => ({
        key,
        line: `${key}: ${asText(item)}`,
        counterpart: key in otherRecord ? `${key}: ${asText(otherRecord[key])}` : undefined,
      }))
      .filter((entry) => !onlyChanges || entry.counterpart !== entry.line);
    const text = asText(content);
    const otherText = asText(otherRecord.content);
    // A content key only one side has counts as a change, even when it is empty.
    const contentChanged = text !== otherText || 'content' in value !== 'content' in otherRecord;
    const showContent = 'content' in value && (!onlyChanges || contentChanged);
    return (
      <div className="space-y-1 text-xs">
        {showContent && (
          <p className="wrap-anywhere whitespace-pre-wrap">
            {contentChanged && text === otherText ? (
              <ChangedText text={text} counterpart={undefined} side={side} />
            ) : (
              <TextDiff text={text} other={otherText} side={side} onlyChanges={onlyChanges} />
            )}
          </p>
        )}
        {onlyChanges && !showContent && keys.length === 0 ? <NothingHere side={side} /> : null}
        {keys.map(({ key, line, counterpart }) => (
          <p key={key} className="text-muted-foreground font-mono wrap-anywhere">
            {counterpart === line ? (
              line
            ) : (
              <ChangedText text={line} counterpart={counterpart} side={side} />
            )}
          </p>
        ))}
      </div>
    );
  }
  return (
    <p className="text-xs wrap-anywhere whitespace-pre-wrap">
      <TextDiff text={asText(value)} other={asText(other)} side={side} onlyChanges={onlyChanges} />
    </p>
  );
}

/** One side of a before / after pair: caption over the value, scrolling past a fixed height. */
function ValuePanel({
  caption,
  tone,
  testId,
  ...view
}: {
  /** Automation hook for the panel root. */
  testId: string;
  caption: string;
  value: unknown;
  other: unknown;
  tone: Side;
  onlyChanges: boolean;
}) {
  return (
    <div
      data-testid={testId}
      className={cn(
        'min-w-0 space-y-1 rounded-md border px-2.5 py-2',
        tone === 'before' ? 'bg-muted/40' : 'bg-success-50 border-success/30',
      )}
    >
      <p className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
        {caption}
      </p>
      {/* pb-1: a scroll container clips at its padding box, so without it the last line's
          descenders and underscores (`trained_algorithmic_media`) are cut off. */}
      <div className="text-foreground max-h-64 overflow-y-auto pb-1">
        <ValueView {...view} side={tone} />
      </div>
    </div>
  );
}

/** The plain-language explanation key for one of the normaliser's action labels (its own
 *  wording, from integration-service), with the shipped field a mirror copies. */
function whyKey(action: string): { key: string; field?: string } {
  const mirrored = /^mirrored to the shipped (\w+)$/.exec(action);
  if (mirrored) return { key: 'mirrored', field: mirrored[1] ?? '' };
  const table: [RegExp, string][] = [
    [/^stripped leaked disclosure wrapper/, 'wrapper'],
    [/^stripped HTML/, 'html'],
    [/^whitespace \/ control characters/, 'whitespace'],
    [/^emptied status-note value/, 'statusNote'],
    [/^added missing digital_source_type/, 'disclosureAdded'],
    [/^corrected invalid digital_source_type/, 'disclosureCorrected'],
    [/^unquoted digital_source_type/, 'disclosureUnquoted'],
    [/^dropped unknown keys/, 'structuredKeys'],
    [/^rebuilt content collapsed/, 'structuredRebuilt'],
    [/^split one item into/, 'listSplit'],
    [/^flattened an object stored inside a list item/, 'listObject'],
    [/^cleaned items/, 'listCleaned'],
    [/^canonical row keys/, 'detailKeys'],
    [/^dropped status-note row/, 'detailStatusNote'],
    [/^dropped a row with no attribute name or value/, 'detailEmptyRow'],
    [/^split a pasted section/, 'detailPastedName'],
    [/^realigned a value shifted/, 'detailShifted'],
    [/^flattened a nested attributes group/, 'detailNested'],
    [/^expanded a detail list/, 'detailExpanded'],
    [/^decoded a row sent as a JSON string/, 'detailDecoded'],
    [/^dropped a non-object row/, 'detailNonObject'],
  ];
  return { key: table.find(([pattern]) => pattern.test(action))?.[1] ?? 'generic' };
}

/** A field's distinct explanations, in the order its actions were taken. */
function reasonsOf(actions: string[]): [string, { key: string; field?: string }][] {
  const reasons = new Map<string, { key: string; field?: string }>();
  for (const action of actions) {
    const why = whyKey(action);
    reasons.set(`${why.key}:${why.field ?? ''}`, why);
  }
  return [...reasons.entries()];
}

/** Before / after of each field the validation normalised, with the agents' normaliser's own
 *  wording of what it did; "Show only changes" keeps just the lines, items and rows that
 *  differ. `isApplied`: code already wrote it (the product is valid now). */
export function FieldChangeList({
  changes,
  isApplied,
  fieldLabel,
}: {
  changes: EnrichmentValidationFieldChange[];
  isApplied: boolean;
  fieldLabel: (field: string) => string;
}) {
  const { t } = useTranslation('catalog-operations');
  // Per product (component state): one product's choice does not move the others.
  const [onlyChanges, setOnlyChanges] = useState(false);
  const heading = isApplied
    ? t('enrichmentValidation.drawer.changes.applied')
    : t('enrichmentValidation.drawer.changes.pending');
  return (
    <section
      aria-label={heading}
      data-testid="batch-detail-validation-drawer-changes"
      className="space-y-2"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <p className="text-primary text-xs font-medium">{heading}</p>
        <Button
          type="button"
          variant={onlyChanges ? 'default' : 'outline'}
          size="sm"
          aria-pressed={onlyChanges}
          data-testid="batch-detail-validation-drawer-changes-only-changes-toggle"
          className="h-7 cursor-pointer gap-1.5 px-2.5 text-xs"
          onClick={() => {
            setOnlyChanges((value) => !value);
          }}
        >
          {onlyChanges ? (
            <Rows3 aria-hidden="true" className="size-3.5" />
          ) : (
            <ScanLine aria-hidden="true" className="size-3.5" />
          )}
          {onlyChanges
            ? t('enrichmentValidation.drawer.changes.showFull')
            : t('enrichmentValidation.drawer.changes.showOnlyChanges')}
        </Button>
      </div>
      <p className="text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
        <mark className={MARK_CLASS.before}>
          {t('enrichmentValidation.drawer.changes.removed')}
        </mark>
        <mark className={MARK_CLASS.after}>{t('enrichmentValidation.drawer.changes.added')}</mark>
        <span>{t('enrichmentValidation.drawer.changes.legend')}</span>
      </p>
      <ul className="space-y-3">
        {changes.map((change) => {
          const leftAsStored = !change.applied;
          return (
            // A field is normalised at most once per verdict, so it is unique in the list.
            <li
              key={change.field}
              data-testid={`batch-detail-validation-drawer-changes-item-${change.field}`}
              className="space-y-1.5"
            >
              <div className="flex flex-wrap items-center gap-1.5">
                <p className="text-foreground text-xs font-semibold">{fieldLabel(change.field)}</p>
                {change.actions.map((action) => (
                  <span
                    key={action}
                    className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-[11px]"
                  >
                    {action}
                  </span>
                ))}
              </div>
              <ul className="text-muted-foreground space-y-0.5 text-xs">
                {reasonsOf(change.actions).map(([id, why]) => (
                  <li key={id}>
                    <span className="text-foreground font-medium">
                      {t('enrichmentValidation.drawer.changes.why.label')}
                    </span>{' '}
                    {t(`enrichmentValidation.drawer.changes.why.${why.key}`, {
                      field: why.field ? fieldLabel(why.field).toLowerCase() : '',
                    })}
                  </li>
                ))}
              </ul>
              {change.truncated ? (
                <p
                  data-testid={`batch-detail-validation-drawer-changes-truncated-${change.field}`}
                  className="text-muted-foreground text-xs"
                >
                  {t('enrichmentValidation.drawer.changes.truncated')}
                </p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  <ValuePanel
                    testId={`batch-detail-validation-drawer-changes-before-${change.field}`}
                    caption={t('enrichmentValidation.drawer.changes.before')}
                    value={change.before}
                    other={change.after}
                    tone="before"
                    onlyChanges={onlyChanges}
                  />
                  <ValuePanel
                    testId={`batch-detail-validation-drawer-changes-after-${change.field}`}
                    caption={t('enrichmentValidation.drawer.changes.after')}
                    value={change.after}
                    other={change.before}
                    tone="after"
                    onlyChanges={onlyChanges}
                  />
                </div>
              )}
              {leftAsStored && (
                <p
                  data-testid={`batch-detail-validation-drawer-changes-left-as-stored-${change.field}`}
                  className="text-warning text-xs"
                >
                  {t('enrichmentValidation.drawer.changes.leftAsStored')}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
